/* eslint-disable max-classes-per-file, no-underscore-dangle, no-param-reassign, jsx-a11y/media-has-caption */
import React from 'react';
import { Cookie } from 'next-cookie';
import axios from 'axios';

import { useSelector } from 'react-redux';
import shaka from 'shaka-player/dist/shaka-player.ui';

// Hooks
import useAuth from '../../hooks/useAuth';

// eslint-disable-next-line import/no-unresolved
import { JAVA_API_BASE_URL } from '../../constants/api';
import { javaApi } from '../../utils/AxiosInstance';

import WaterMarkComponent from './WaterMark';

// Shaka UI Components
import '../../utils/shaka/ui/VideoQualitySelection';

const { FairPlayUtils } = shaka.util;

class SeekBackButton extends shaka.ui.Element {
  constructor(parent, controls) {
    super(parent, controls);

    // The actual button that will be displayed
    this.button_ = document.createElement('button');
    this.button_.classList.add('material-icons-round');
    this.button_.classList.add('shaka-rewind-5-button');
    this.button_.textContent = 'replay_5';
    this.button_.ariaLabel = 'Rewind 5';
    this.parent.appendChild(this.button_);

    // Listen for clicks on the button to start the next playback
    this.eventManager.listen(this.button_, 'click', () => {
      this.video.currentTime -= 5;
    });
  }
}

SeekBackButton.Factory = class {
  // eslint-disable-next-line class-methods-use-this
  create(rootElement, controls) {
    return new SeekBackButton(rootElement, controls);
  }
};

shaka.ui.Controls.registerElement('seek_back', new SeekBackButton.Factory());

class SeekNextButton extends shaka.ui.Element {
  constructor(parent, controls) {
    super(parent, controls);

    // The actual button that will be displayed
    this.button_ = document.createElement('button');
    this.button_.classList.add('material-icons-round');
    this.button_.classList.add('shaka-forward-5-button');
    this.button_.textContent = 'forward_5';
    this.button_.ariaLabel = 'Forward 5';
    this.parent.appendChild(this.button_);

    // Listen for clicks on the button to start the next playback
    this.eventManager.listen(this.button_, 'click', () => {
      this.video.currentTime += 5;
    });
  }
}

SeekNextButton.Factory = class {
  // eslint-disable-next-line class-methods-use-this
  create(rootElement, controls) {
    return new SeekNextButton(rootElement, controls);
  }
};

shaka.ui.Controls.registerElement('seek_next', new SeekNextButton.Factory());

function ShakaPlayer({
  video: { drmProvider: videoProvider, videoUrl: videoSrc, referenceId, isDrmEnabled },
  courseId,
  className,
  playerRef,
  triggerEvent,
  updateVideoState,
  ...rest
}) {
  const { authToken } = useAuth();

  const isSafari = React.useRef(
    navigator.userAgent.indexOf('Safari') !== -1 && navigator.userAgent.indexOf('Chrome') === -1,
  );
  const contentId = React.useRef(null);
  const uiContainerRef = React.useRef(null);
  const videoRef = React.useRef(null);
  const cookies = new Cookie();

  const [useNativeHlsOnSafari, setUseNativeHlsOnSafari] = React.useState(true);

  const [player, setPlayer] = React.useState(null);
  const [ui, setUi] = React.useState(null);
  const [src, setSrc] = React.useState(null);

  const onShakaError = React.useCallback((error) => {
    if (error.detail.code === 3016) {
      setUseNativeHlsOnSafari(false);
    }
  }, [setUseNativeHlsOnSafari]);

  // Effect to handle component mount & mount.
  // Not related to the src prop, this hook creates a shaka.Player instance.
  // This should always be the first effect to run.
  React.useEffect(() => {
    const shakaPlayer = new shaka.Player(videoRef.current);
    if (shakaPlayer) {
      shakaPlayer.addEventListener('error', onShakaError);
    }

    setPlayer(shakaPlayer);

    const playerUI = new shaka.ui.Overlay(shakaPlayer, uiContainerRef.current, videoRef.current);

    playerUI.configure({
      overflowMenuButtons: ['quality_human', 'language', 'playback_rate'],
      controlPanelElements: [
        'seek_back',
        'play_pause',
        'seek_next',
        'time_and_duration',
        'spacer',
        'mute',
        'volume',
        'fullscreen',
        'overflow_menu',
      ],
      enableKeyboardPlaybackControls: true,
      seekBarColors: {
        base: 'rgba(255, 255, 255, 0.3)',
        buffered: 'rgba(255, 255, 255, 0.54)',
        played: 'rgb(255, 255, 255)',
      },
    });

    setUi(playerUI);

    return () => {
      shakaPlayer.removeEventListener('error', onShakaError);
      shakaPlayer.destroy();
      if (playerUI) {
        playerUI.destroy();
      }
    };
  }, [onShakaError]);

  async function getFpsCertificate(fairplayCertUrl) {
    const req = await fetch(fairplayCertUrl);
    const cert = await req.arrayBuffer();
    return new Uint8Array(cert);
  }

  function getNonSafariPlayerConfig(widevineLicenseServer, playreadyLicenseServer) {
    return {
      drm: {
        servers: {
          'com.widevine.alpha': widevineLicenseServer,
          'com.microsoft.playready': playreadyLicenseServer,
        },
      },
    };
  }

  // eslint-disable-next-line function-paren-newline
  const getSafariPlayerConfig = React.useCallback(
    // eslint-disable-next-line no-shadow
    (videoProvider, fairplayLicenseServerUrl, serverCertificate) => ({
      drm: {
        servers: {
          'com.apple.fps.1_0': fairplayLicenseServerUrl,
        },
        advanced: {
          'com.apple.fps.1_0': {
            serverCertificate,
          },
        },
        initDataTransform(initData, initDataType) {
          if (initDataType !== 'skd') return initData;

          // Set the Fairplay license server URL with the one from the HLS manifest
          // eslint-disable-next-line no-param-reassign
          const skdUri = shaka.util.StringUtils.fromBytesAutoDetect(initData);

          // Create the initData for Fairplay
          if (videoProvider.toLowerCase() === 'jwplayer') {
            contentId.current = skdUri.split('/').pop();
          } else {
            contentId.current = skdUri.split('skd://')[1].substr(0, 32);
          }

          return initData;

          // return shaka.util.FairPlayUtils.initDataTransform(
          //   initData,
          //   contentId.current,
          //   drmInfo.serverCertificate,
          // );
        },
      },
    }),
    [],
  );

  // eslint-disable-next-line no-shadow
  const loadDRMAsset = React.useCallback(
    async (shakaPlayer, finalVideoUrl) => {
      try {
        const authTokenServer = new URL(`${JAVA_API_BASE_URL}app-content-ws/v1/drm/get/license?drm-type=DRM_TYPE`);

        authTokenServer.searchParams.append('course_id', courseId);
        authTokenServer.searchParams.append('videoUuid', referenceId);
        authTokenServer.searchParams.append('drm_provider', videoProvider);

        let widevineLicenseServer = authTokenServer.toString().replace('DRM_TYPE', 'widevine');
        let playreadyLicenseServer = authTokenServer.toString().replace('DRM_TYPE', 'playready');
        let fairplayLicenseServer = authTokenServer.toString().replace('DRM_TYPE', 'fairplay');
        let fairplayCertUrl = 'https://fp-keyos.licensekeyserver.com/cert/2b8db8748b79535edd52bbc1495d6efe.der';

        if (referenceId && videoProvider.toLowerCase() === 'jwplayer') {
          try {
            const jwPlayerJavaResponse = await javaApi.get('app-content-ws/v1/drm/get/license/jwplayer', {
              params: {
                course_id: courseId,
                media_id: referenceId,
              },
            });

            if (jwPlayerJavaResponse.data.success) {
              const mediaDrmData = await axios.get(jwPlayerJavaResponse.data.response);

              if (mediaDrmData.status === 200 && mediaDrmData?.data?.playlist.length) {
                const playlistVideo = mediaDrmData?.data?.playlist[0];

                widevineLicenseServer = playlistVideo.sources[0].drm.widevine.url;
                playreadyLicenseServer = playlistVideo.sources[0].drm.playready.url;
                fairplayLicenseServer = playlistVideo.sources[1].drm.fairplay.processSpcUrl;
                fairplayCertUrl = playlistVideo.sources[1].drm.fairplay.certificateUrl;

                finalVideoUrl = playlistVideo.sources[0].file;
                if (isSafari.current) {
                  finalVideoUrl = playlistVideo.sources[1].file;
                }
              }
            }
          } catch (error) {
            //
          }
        }

        // Clear All Request and Response Filters
        shakaPlayer.getNetworkingEngine().clearAllRequestFilters();
        shakaPlayer.getNetworkingEngine().clearAllResponseFilters();

        // Add Request Filter to add Auth Header
        shakaPlayer.getNetworkingEngine().registerRequestFilter((type, request) => {
          if (type !== shaka.net.NetworkingEngine.RequestType.LICENSE) return;

          request.headers.Authorization = `Bearer ${authToken}`;
        });

        let playerConfig = getNonSafariPlayerConfig(widevineLicenseServer, playreadyLicenseServer);
        if (isSafari.current) {
          const fairplayCert = await getFpsCertificate(fairplayCertUrl);
          playerConfig = getSafariPlayerConfig(videoProvider, fairplayLicenseServer, fairplayCert);

          finalVideoUrl = finalVideoUrl.replace(/dash2?/, 'hls').replace('mpd', 'm3u8');

          // License Request and Response Filters
          if (videoProvider.toLowerCase() === 'jwplayer') {
            shakaPlayer.getNetworkingEngine().registerRequestFilter(FairPlayUtils.ezdrmFairPlayRequest);
            shakaPlayer.getNetworkingEngine().registerResponseFilter(FairPlayUtils.commonFairPlayResponse);
          } else {
            // Request Filter
            shakaPlayer.getNetworkingEngine().registerRequestFilter((type, request) => {
              // ignore requests that are not license requests.
              if (type !== shaka.net.NetworkingEngine.RequestType.LICENSE) return;

              // custom fairplay license request body required
              request.headers.Authorization = `Bearer ${authToken}`;

              request.headers['Content-Type'] = 'text/plain';
              const originalPayload = new Uint8Array(request.body);
              const base64Payload = shaka.util.Uint8ArrayUtils.toStandardBase64(originalPayload);
              const params = `spc=${base64Payload}`;
              request.body = shaka.util.StringUtils.toUTF8(params);
            });

            // Response Filter
            shakaPlayer.getNetworkingEngine().registerResponseFilter((type, response) => {
              if (type !== shaka.net.NetworkingEngine.RequestType.LICENSE) return;

              let responseText = shaka.util.StringUtils.fromUTF8(response.data);
              // Trim whitespace.
              responseText = responseText.trim();

              // Decode the base64-encoded data into the format the browser expects.
              response.data = shaka.util.Uint8ArrayUtils.fromBase64(responseText).buffer;
            });
          }
        }

        // Configure player for DRM
        shakaPlayer.configure(playerConfig);

        // Setting Player video URL
        setSrc(finalVideoUrl);
      } catch (e) {
        //
      }
    },
    [authToken, courseId, getSafariPlayerConfig, referenceId, videoProvider],
  );

  React.useEffect(() => {
    if (player && isDrmEnabled) {
      shaka.polyfill.installAll();
      if (shaka.Player.isBrowserSupported()) {
        loadDRMAsset(player, videoSrc);
      }
    } else if (player && !isDrmEnabled) {
      setSrc(videoSrc);
    } else if (player) {
      setSrc(videoSrc);
    }

    return () => {
      setUseNativeHlsOnSafari(true);
    };
  }, [player, videoSrc, isDrmEnabled, loadDRMAsset]);

  // play and pause on pressing space bar
  const handleKeyPress = React.useCallback((event) => {
    const video = videoRef.current;
    if (
      document.activeElement.tagName.toLowerCase() === 'body' ||
      document.activeElement.tagName.toLowerCase() === 'button'
    ) {
      if (event.key === ' ') {
        event.preventDefault();
        if (video.paused) {
          playerRef.current.playVideo(true);
        } else {
          playerRef.current.pauseVideo(true);
        }
      } else if (event.which === 74) {
        event.preventDefault();
        video.currentTime -= 5;
      } else if (event.which === 76) {
        event.preventDefault();
        video.currentTime += 5;
      } else if (event.which === 77) {
        event.preventDefault();
        video.muted = !video.muted;
      }
    }
  }, [playerRef]);

  React.useEffect(() => {
    document.addEventListener('keydown', handleKeyPress);
    return () => {
      document.removeEventListener('keydown', handleKeyPress);
    };
  }, [handleKeyPress]);

  // Load the source url when we have one.
  React.useEffect(() => {
    if (player && src) {
      player.configure('streaming.useNativeHlsOnSafari', useNativeHlsOnSafari);
      player.load(src).then(() => {
        // Load Shaka Config
        const playerConfig = window.sessionStorage.getItem('playerConfig');
        if (playerConfig) {
          const { playbackRate, playbackResolution } = JSON.parse(playerConfig);

          playerRef.current.videoElement.playbackRate = playbackRate;
          if (playbackResolution === null) {
            playerRef.current.player.configure({ abr: { enabled: true } });
          } else {
            playerRef.current.player.configure({ abr: { enabled: false } });
            const lastResolution = playerRef.current.player.getVariantTracks()
              .find((track) => track.originalVideoId === playbackResolution);

            if (lastResolution?.originalVideoId) {
              playerRef.current.player.selectVariantTrack(lastResolution, true);
            }
          }
        }
      });
    }
  }, [player, src, useNativeHlsOnSafari]);

  // Define a handle for easily referencing Shaka's player & ui API's.
  React.useImperativeHandle(
    playerRef,
    () => ({
      get player() {
        return player;
      },
      get ui() {
        return ui;
      },
      get videoElement() {
        return videoRef.current;
      },
    }),
    [player, ui],
  );

  const waterMark = useSelector((state) => state.auth.userPhoneNumber || state.auth.authUser?.mobileNumber);

  const waterMarkText = waterMark || cookies.get('mobileNumber');

  return (
    <div ref={uiContainerRef} className={className}>
      <video
        ref={videoRef}
        style={{
          maxWidth: '100%',
          width: '100%',
        }}
        {...rest}
      />
      <WaterMarkComponent text={waterMarkText} containerRef={videoRef} />
    </div>
  );
}

export default ShakaPlayer;
